import { Directive } from '@angular/core';
import { getBusinessCompanyThingValueLabel } from 'src/app/_enums/business/business-company-thing-value.enum';
import {
  BusinessInsuredObject,
  getBusinessInsuredObjectLabel,
} from 'src/app/_enums/business/business-insured-object.enum';
import {
  BusinessObjectsCover,
  getBusinessObjectsCoverLabel,
} from 'src/app/_enums/business/business-object-cover.enum';
import { getBooleanLabel } from 'src/app/_helpers/utils';
import {
  BusinessCompanyThingsBasicCoverage,
  BusinessThingsMetadata,
} from 'src/app/_types/lead.types';

import { BaseMetadataComponent } from '../../base-metadata.component';

@Directive()
export abstract class BusinessThingsMetadataComponentAbstract extends BaseMetadataComponent<BusinessThingsMetadata> {
  getBooleanLabel = getBooleanLabel;

  getBusinessInsuredObjectLabel = getBusinessInsuredObjectLabel;

  getBusinessObjectsCoverLabel = getBusinessObjectsCoverLabel;

  getBusinessCompanyThingValueLabel = getBusinessCompanyThingValueLabel;

  getInsuredObjectsText(objects?: BusinessInsuredObject[]): string {
    if (!objects?.length) return '';
    return objects
      .map((object) => getBusinessInsuredObjectLabel(object))
      .join(', ');
  }

  getCoversText(covers?: BusinessObjectsCover[]): string {
    if (!covers?.length) return '';
    return covers
      .map((cover) => getBusinessObjectsCoverLabel(cover))
      .join(', ');
  }

  hasBasicInfo(): boolean {
    return (
      this.metadata?.reimbursedSum !== undefined ||
      this.metadata?.insuredSum !== undefined ||
      this.metadata?.insuredObjects?.length > 0 ||
      this.metadata?.otherObject !== undefined
    );
  }

  hasCurrentInsuranceInfo(): boolean {
    return (
      (this.metadata?.hasInsurance !== null &&
        this.metadata?.hasInsurance !== undefined) ||
      this.metadata?.currentInsurer !== undefined ||
      (this.metadata?.hasClaimsLastFiveYears !== null &&
        this.metadata?.hasClaimsLastFiveYears !== undefined)
    );
  }

  isFireDamageCoverageEmpty(
    coverage?: BusinessThingsMetadata['fireDamageCoverage']
  ): boolean {
    if (!coverage) return true;
    return (
      coverage.fireNaturalDisasterCoverage === undefined &&
      coverage.dicDilCoverage === undefined &&
      coverage.insuredSum === undefined &&
      coverage.value === undefined &&
      coverage.fireDeductible === undefined &&
      coverage.specialFees === undefined &&
      coverage.contingencyInsurance === undefined &&
      coverage.externalInsurance === undefined &&
      coverage.constructionHutTools === undefined &&
      coverage.internalDisturbances === undefined &&
      coverage.internalDistrubancesDeductible === undefined &&
      coverage.monetaryValues === undefined &&
      coverage.monetaryValuesInSafe === undefined
    );
  }

  isBasicCoverageEmpty(coverage?: BusinessCompanyThingsBasicCoverage): boolean {
    if (!coverage) return true;
    return (
      coverage.coverage === undefined &&
      coverage.insuredSum === undefined &&
      coverage.deductible === undefined &&
      coverage.value === undefined
    );
  }

  isTheftCoverageEmpty(
    coverage?: BusinessThingsMetadata['theftCoverage']
  ): boolean {
    if (!coverage) return true;
    return (
      coverage.theftCoverage === undefined &&
      coverage.insuredSum === undefined &&
      coverage.value === undefined &&
      coverage.deductible === undefined &&
      coverage.specialFees === undefined &&
      coverage.contingencyInsurance === undefined &&
      coverage.externalInsurance === undefined &&
      coverage.constructionHutTools === undefined &&
      coverage.monetaryValues === undefined &&
      coverage.monetaryValuesInSafe === undefined
    );
  }

  isWaterDamageCoverageEmpty(
    coverage?: BusinessThingsMetadata['waterDamageCoverage']
  ): boolean {
    if (!coverage) return true;
    return (
      coverage.waterDamageCoverage === undefined &&
      coverage.insuredSum === undefined &&
      coverage.value === undefined &&
      coverage.deductible === undefined &&
      coverage.specialFees === undefined &&
      coverage.contingencyInsurance === undefined &&
      coverage.externalInsurance === undefined &&
      coverage.constructionHutTools === undefined
    );
  }

  isEpidemicCoverageEmpty(
    coverage?: BusinessThingsMetadata['epidemicCoverage']
  ): boolean {
    if (!coverage) return true;
    return (
      coverage.pandemicIncluded === undefined &&
      coverage.insuredSum === undefined &&
      coverage.deductible === undefined &&
      coverage.duration === undefined
    );
  }

  isTechnicalCoverageEmpty(
    coverage?: BusinessThingsMetadata['technicalCoverage']
  ): boolean {
    if (!coverage) return true;
    return (
      coverage.technicalCoverage === undefined &&
      coverage.insuredSum === undefined &&
      coverage.deductible === undefined &&
      coverage.insuranceType === undefined
    );
  }

  isLossCoverageEmpty(
    coverage?: BusinessThingsMetadata['lossCoverage']
  ): boolean {
    if (!coverage) return true;
    return (
      coverage.lossCoverage === undefined &&
      coverage.fireNaturalDisasterCoverage === undefined &&
      coverage.fireInsuredSum === undefined &&
      coverage.fireDeductible === undefined &&
      coverage.theftCoverage === undefined &&
      coverage.theftInsuredSum === undefined &&
      coverage.theftDeductible === undefined &&
      coverage.waterDamageCoverage === undefined &&
      coverage.waterInsuredSum === undefined &&
      coverage.waterDeductible === undefined &&
      coverage.revenue === undefined &&
      coverage.grossInsuranceProfit === undefined
    );
  }
}
